import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, ArrowRight, Pencil, Trash2 } from 'lucide-react';
import type { WebsiteIdea } from '@/types/website';
import { WEBSITE_LABELS, IDEA_STATUS_LABELS } from '@/types/website';
import { cn } from '@/lib/utils';

interface IdeaTileProps {
  idea: WebsiteIdea;
  onEdit?: () => void;
  onDelete?: () => void;
  onConvertToTask?: () => void;
  canEdit?: boolean;
}

const statusColors: Record<string, string> = {
  nieuw: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  in_overweging: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  goedgekeurd: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  afgewezen: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const websiteColors: Record<string, string> = {
  greenvision: 'border-t-green-500',
  gv_charging: 'border-t-blue-500',
};

export function IdeaTile({ idea, onEdit, onDelete, onConvertToTask, canEdit }: IdeaTileProps) {
  return (
    <Card className={cn('border-t-4 h-full flex flex-col', websiteColors[idea.website])}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-sm line-clamp-2">{idea.title}</h3>
          {idea.impact_score && (
            <div className="flex items-center gap-0.5 shrink-0 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded text-xs font-medium">
              <Star className="h-3 w-3 fill-current" />
              {idea.impact_score}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col">
        {idea.description && (
          <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
            {idea.description}
          </p>
        )}

        <div className="flex flex-wrap gap-1 mb-3">
          <Badge variant="outline" className="text-xs">
            {WEBSITE_LABELS[idea.website]}
          </Badge>
          <Badge variant="outline" className={cn('text-xs', statusColors[idea.status])}>
            {IDEA_STATUS_LABELS[idea.status]}
          </Badge>
        </div>

        {idea.next_step && (
          <div className="text-xs text-muted-foreground mb-3">
            <span className="font-medium">Volgende stap:</span>{' '}
            <span className="line-clamp-2">{idea.next_step}</span>
          </div>
        )}

        <div className="flex items-center justify-between mt-auto pt-2 border-t">
          {idea.status === 'goedgekeurd' && onConvertToTask && (
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={onConvertToTask}>
              <ArrowRight className="h-3 w-3 mr-1" />
              Naar taak
            </Button>
          )}
          {canEdit && (
            <div className="flex gap-1 ml-auto">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
